import React, { useState, useCallback } from 'react'
import {
  Drawer as ChakraDrawer,
  DrawerOverlay,
  DrawerContent,
  Text,
  Box,
  DrawerCloseButton,
  DrawerFooter,
  VStack,
  Textarea,
  List,
  ListItem,
  Button,
  useToast,
  Flex
} from '@chakra-ui/react'
import { BetaBadge } from '../../../components/beta-badge'
import { useParams } from 'react-router-dom'
import { MagicWand, Refresh } from '@blueprinthq/joy'
import { useMutation, useQueryClient } from 'react-query'
import { getSessionControllerGetProgressNotesForSessionQueryKey } from '@core/api'
import { endpoints } from '@api'

export const MagicEditDrawer = ({
  isOpen,
  closeDrawer,
  progressNoteId
}: {
  isOpen: boolean
  closeDrawer: () => void
  progressNoteId: string
}) => {
  const { sessionId }: { sessionId: string } = useParams()
  const queryClient = useQueryClient()
  const toast = useToast()

  const [userInstructions, setUserInstructions] = useState('')

  const { mutateAsync: executeRegenerateProgressNote, isLoading } = useMutation(
    endpoints.postRegenerateProgressNote.request,
    {
      onError: () => {
        toast({
          title: 'Error',
          description: 'There was an error regenerating this progress note',
          status: 'error',
          isClosable: true,
          duration: 2000,
          position: 'bottom-left'
        })
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getSessionControllerGetProgressNotesForSessionQueryKey(sessionId)
        )
      }
    }
  )

  const submitRegenerateProgressNote = useCallback(async () => {
    const data = {
      progressNoteId,
      userInstructions
    }
    await executeRegenerateProgressNote(data)

    closeDrawer()
  }, [progressNoteId, userInstructions])

  return (
    <ChakraDrawer isOpen={isOpen} onClose={closeDrawer} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <Box
          height="95px"
          backgroundColor="primary"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px="small"
        >
          <Flex w="150px" align="center" justify="space-between">
            <MagicWand fill="white" />
            <Text as="span" fontWeight="bold" color="white">
              Magic edit
            </Text>
            <BetaBadge />
          </Flex>
          <DrawerCloseButton color="white" position="static" />
        </Box>
        <Box p="small" overflowY="scroll" height="100%">
          <VStack spacing="small" alignItems="flex-start">
            <Text>
              Something missing or wrong in this note? Tell our AI what to fix:
            </Text>
            <Textarea
              h="140px"
              placeholder="Enter your instructions here..."
              borderColor={'lightgray'}
              onChange={e => setUserInstructions(e.target.value)}
            ></Textarea>
            <Box bg="#2D54E80A" borderRadius="sm" p="xsmall" pl="large">
              <Text textAlign="center" fontWeight="bold">
                Example instructions
              </Text>
              <List styleType="disc">
                <ListItem>Make the subjective section longer</ListItem>
                <ListItem>Remove direct client quotes</ListItem>
                <ListItem>Refer to the writer as "psychologist"</ListItem>
                <ListItem>
                  Write out more specific interventions based on transcript
                </ListItem>
                <ListItem>Include more language on CBT</ListItem>
                <ListItem>
                  Document the risk assessment, include that ct did not exhibit
                  any SI
                </ListItem>
                <ListItem>
                  Document more symptoms based on the diagnosis of xxx
                </ListItem>
              </List>
            </Box>
          </VStack>
        </Box>
        <DrawerFooter mt="small" p={0} width="100%">
          <Box
            m="small"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
          >
            <Button
              colorScheme="primary"
              variant="solid"
              type="submit"
              onClick={submitRegenerateProgressNote}
              size="lg"
              w="100%"
              isLoading={isLoading}
              isDisabled={!userInstructions.trim()}
            >
              <Flex w="155px" align="center" justify="space-between">
                <Refresh fill="white" />
                Regenerate note
              </Flex>
            </Button>
          </Box>
        </DrawerFooter>
      </DrawerContent>
    </ChakraDrawer>
  )
}
